import React, { useState, useRef } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import { contactAPI } from '../services/api';
import Toast from './Toast';
import { useLang } from '../context/LanguageContext';
import '../styles/ContactModal.css';

const ContactModal = ({ isOpen, onClose }) => {
  const { nl } = useLang();
  const [formData, setFormData] = useState({
    firstName: '', lastName: '', email: '', phone: '', company: '', service: '', message: ''
  });
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [captchaValue, setCaptchaValue] = useState(null);
  const [showToast, setShowToast] = useState(false);
  const recaptchaRef = useRef();

  if (!isOpen) return null;

  const validateName = (name) => /^[a-zA-Z\s'-]{2,}$/.test(name.trim());
  const validateEmail = (email) => /^[a-zA-Z][a-zA-Z0-9._%+-]*@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(email);

  const validate = (field, value) => {
    const newErrors = { ...errors };
    switch (field) {
      case 'firstName':
        if (!value.trim()) newErrors.firstName = nl ? 'Voornaam is verplicht' : 'First name is required';
        else if (!validateName(value)) newErrors.firstName = nl ? 'Alleen letters, minimaal 2 tekens' : 'Only letters, minimum 2 characters';
        else delete newErrors.firstName;
        break;
      case 'lastName':
        if (!value.trim()) newErrors.lastName = nl ? 'Achternaam is verplicht' : 'Last name is required';
        else if (!validateName(value)) newErrors.lastName = nl ? 'Alleen letters, minimaal 2 tekens' : 'Only letters, minimum 2 characters';
        else delete newErrors.lastName;
        break;
      case 'email':
        if (!value.trim()) newErrors.email = nl ? 'E-mail is verplicht' : 'Email is required';
        else if (!validateEmail(value)) newErrors.email = nl ? 'Ongeldig e-mailadres (bijv. naam@domein.com)' : 'Invalid email (e.g. name@domain.com)';
        else delete newErrors.email;
        break;
      default:
        break;
    }
    setErrors(newErrors);
    return newErrors;
  };

  const handleChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
    if (touched[field]) validate(field, value);
  };

  const handleBlur = (field) => {
    setTouched({ ...touched, [field]: true });
    validate(field, formData[field]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    setTouched({ firstName: true, lastName: true, email: true });
    const e1 = validate('firstName', formData.firstName);
    const e2 = validate('lastName', formData.lastName);
    const e3 = validate('email', formData.email);
    const allErrors = { ...e1, ...e2, ...e3 };
    if (Object.keys(allErrors).length > 0) return;

    if (process.env.REACT_APP_RECAPTCHA_SITE_KEY && !captchaValue) {
      alert(nl ? 'Voltooi de reCAPTCHA-verificatie' : 'Please complete the reCAPTCHA verification');
      return;
    }
    setSubmitting(true);
    try {
      await contactAPI.submit({ ...formData, captcha: captchaValue });
      setShowToast(true);
      setFormData({ firstName: '', lastName: '', email: '', phone: '', company: '', service: '', message: '' });
      setErrors({});
      setTouched({});
      setCaptchaValue(null);
      if (recaptchaRef.current) recaptchaRef.current.reset();
      setTimeout(() => onClose(), 1000);
    } catch (error) {
      alert(nl ? 'Fout bij verzenden. Probeer het opnieuw.' : 'Error sending message. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const errorStyle = { color: '#dc3545', fontSize: '12px', marginTop: '4px', display: 'block' };

  return (
    <>
      {showToast && <Toast message={nl ? "Formulier succesvol verzonden!" : "Form submitted successfully!"} onClose={() => setShowToast(false)} />}
      {isOpen && (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <button className="modal-close" onClick={onClose}>&times;</button>
        <h3>{nl ? 'Neem contact op' : 'Contact Sales'}</h3>
        <form className="contact-form" onSubmit={handleSubmit}>
          <div className="form-row">
            <div className="form-group">
              <label>{nl ? 'Voornaam' : 'First Name'} *</label>
              <input type="text" value={formData.firstName} onChange={(e) => handleChange('firstName', e.target.value)} onBlur={() => handleBlur('firstName')} style={errors.firstName ? {borderColor: '#dc3545'} : {}} required />
              {touched.firstName && errors.firstName && <span style={errorStyle}>{errors.firstName}</span>}
            </div>
            <div className="form-group">
              <label>{nl ? 'Achternaam' : 'Last Name'} *</label>
              <input type="text" value={formData.lastName} onChange={(e) => handleChange('lastName', e.target.value)} onBlur={() => handleBlur('lastName')} style={errors.lastName ? {borderColor: '#dc3545'} : {}} required />
              {touched.lastName && errors.lastName && <span style={errorStyle}>{errors.lastName}</span>}
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>{nl ? 'E-mailadres' : 'Email Address'} *</label>
              <input type="email" value={formData.email} onChange={(e) => handleChange('email', e.target.value)} onBlur={() => handleBlur('email')} style={errors.email ? {borderColor: '#dc3545'} : {}} required />
              {touched.email && errors.email && <span style={errorStyle}>{errors.email}</span>}
            </div>
            <div className="form-group">
              <label>{nl ? 'Telefoonnummer' : 'Phone Number'}</label>
              <input type="tel" value={formData.phone} onChange={(e) => handleChange('phone', e.target.value)} />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>{nl ? 'Bedrijfsnaam' : 'Company Name'}</label>
              <input type="text" value={formData.company} onChange={(e) => handleChange('company', e.target.value)} />
            </div>
            <div className="form-group">
              <label>{nl ? 'Dienst interesse' : 'Service Interest'} *</label>
              <select value={formData.service} onChange={(e) => handleChange('service', e.target.value)} required>
                <option value="">{nl ? 'Selecteer een dienst' : 'Select a service'}</option>
                <option value="ai">{nl ? 'AI-ontwikkeling' : 'AI Development'}</option>
                <option value="blockchain">{nl ? 'Blockchain-ontwikkeling' : 'Blockchain Development'}</option>
                <option value="cloud">{nl ? 'Cloud-oplossingen' : 'Cloud Solutions'}</option>
                <option value="mobile">{nl ? 'Mobiele ontwikkeling' : 'Mobile Development'}</option>
                <option value="web">{nl ? 'Webontwikkeling' : 'Web Development'}</option>
                <option value="bot">{nl ? 'Bot-ontwikkeling' : 'Bot Development'}</option>
                <option value="consulting">{nl ? 'Advies' : 'Consulting'}</option>
                <option value="other">{nl ? 'Anders' : 'Other'}</option>
              </select>
            </div>
          </div>
          <div className="form-group">
            <label>{nl ? 'Bericht' : 'Message'} *</label>
            <textarea rows="3" value={formData.message} onChange={(e) => handleChange('message', e.target.value)} required></textarea>
          </div>
          {process.env.REACT_APP_RECAPTCHA_SITE_KEY && (
            <div className="form-group">
              <ReCAPTCHA ref={recaptchaRef} sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY} onChange={setCaptchaValue} />
            </div>
          )}
          <button type="submit" className="submit-btn" disabled={submitting}>
            {submitting ? (
              <>
                <i className="fas fa-spinner fa-spin"></i>
                {nl ? ' Verzenden...' : ' Sending...'}
              </>
            ) : (
              nl ? 'Bericht verzenden' : 'Send Message'
            )}
          </button>
        </form>
      </div>
    </div>
      )}
    </>
  );
};

export default ContactModal;
